/**
 * Resolver for how a cartesian chart lays out measures that span more than
 * one unit group (e.g. currency and count), so the chart renderer can pick
 * between a single shared axis, a combo chart, or stacked panels.
 */

import type { BarChartOptions, MultiMeasureLayout } from '../types';
import type { MeasureUnitGroup } from './measureUnits';

/**
 * The resolved layout for a cartesian chart's measures.
 *
 * `single` — all measures share one axis (commensurate, or a single
 * measure — never faceted or combo'd).
 * `combo` — value measures as bars on the left axis, the count/number
 * measure as a line on a right axis.
 * `panels` — one vertically-stacked panel per unit group, sharing the
 * x-axis.
 */
export type ResolvedMultiMeasureLayout = 'single' | 'combo' | 'panels';

/**
 * Resolve how a cartesian chart should lay out its measures.
 *
 * Decision tree, in order:
 * 1. A single unit group is always commensurate — never faceted/combo'd.
 * 2. Horizontal bars swap axes, so neither layout applies — preserves
 *    today's behavior.
 * 3. An explicit `'panels'` layout is honored as-is.
 * 4. Exactly two unit groups fit a combo chart's single secondary axis —
 *    combo is the default because it's the layout users know from other
 *    CRMs (PR #12263, issue #12219); panels remain available as an
 *    explicit option.
 * 5. Three or more unit groups can't fit a two-axis combo, so they fall
 *    back to panels. Unreachable today per measureUnits.ts's two-unit
 *    ('currency' | 'number') classification, but must not crash if that
 *    changes.
 */
export function resolveMultiMeasureLayout(args: {
  unitGroups: MeasureUnitGroup[];
  layout: MultiMeasureLayout | undefined;
  barOrientation: BarChartOptions['orientation'] | undefined;
}): ResolvedMultiMeasureLayout {
  const { unitGroups, layout, barOrientation } = args;

  if (unitGroups.length <= 1) {
    return 'single';
  }
  if (barOrientation === 'horizontal') {
    return 'single';
  }
  if (layout === 'panels') {
    return 'panels';
  }
  if (unitGroups.length === 2) {
    return 'combo';
  }
  return 'panels';
}
